import type { ReactNode } from 'react';
import { cleanAnchors, type JobDetail } from '@nest-recruit/shared';
import { Card } from '../../../../components/Card';
import { MoreOptions } from '../../../../components/MoreOptions';
import { useModule } from '../../../../lib/modules';
import { useSaveJobStep } from '../../api/jobs-api';
import { PipelineStep } from '../PipelineStep';
import { StageCounters } from './StageCounters';
import type { StepControls, StepProps } from '../step-props';
import { useListSection, useReportSaveState } from './builder-saves';
import { CriteriaTable, namedCriteria, type CriterionRow } from './CriteriaTable';
import { MatrixTable, namedLines, type MatrixRow } from './MatrixTable';
import { missingAction, sectionMissing } from './SectionMissing';
import styles from './builder.module.css';

interface SectionProps {
  readonly job: JobDetail;
  readonly isReadOnly: boolean;
}

/** F5.21: the criteria table, saved as a whole list each time a cell is. */
export function CriteriaSection({ job, isReadOnly }: SectionProps): ReactNode {
  const saveStep = useSaveJobStep();
  const section = useListSection<CriterionRow>({
    source: 'criteria',
    job,
    initial: job.criteria.map((criterion) => ({
      id: criterion.id,
      label: criterion.label,
      importance: criterion.importance,
      kind: criterion.kind,
      lookFor: criterion.lookFor,
      partlyCounts: criterion.partlyCounts,
      minLevel: criterion.minLevel,
    })),
    send: (rows, version) =>
      saveStep.mutateAsync({
        id: job.id,
        step: 'criteria',
        body: {
          version,
          criteria: namedCriteria(rows).map((row) => ({
            id: row.id,
            label: row.label.trim(),
            importance: row.importance,
            kind: row.kind,
            lookFor: [...row.lookFor],
            partlyCounts: [...row.partlyCounts],
            minLevel: row.minLevel,
          })),
        },
      }),
  });
  return (
    <div className={styles.section}>
      <Card title="Screening criteria" meta="What the ranking looks for in each application and CV" action={missingAction(sectionMissing(job, 'criteria'))}>
        <CriteriaTable rows={section.rows} onCommit={section.commit} onRowsChange={section.setRows} isReadOnly={isReadOnly} />
      </Card>
    </div>
  );
}

/** F5.22: the scoring matrix's rows and their save, shared by the job builder and the Interview step (owner 2026-11-01). */
export function useMatrixSection(job: JobDetail): ReturnType<typeof useListSection<MatrixRow>> {
  const saveStep = useSaveJobStep();
  return useListSection<MatrixRow>({
    source: 'matrix',
    job,
    initial: job.matrix.map((line) => ({ key: line.key, label: line.label, anchors: line.anchors, isCritical: line.isCritical, usedIn: line.usedIn })),
    send: (rows, version) =>
      saveStep.mutateAsync({
        id: job.id,
        step: 'matrix',
        body: {
          version,
          lines: namedLines(rows).map((row) => ({
            key: row.key,
            label: row.label.trim(),
            anchors: cleanAnchors(row.anchors),
            isCritical: row.isCritical,
            usedIn: [...row.usedIn],
          })),
        },
      }),
  });
}

/** Interviewers give one overall mark (ADR 0077); the matrix is HR review's, and the panel's guide (owner 2026-11-01). */
export const MATRIX_NO_ROUNDS_NOTE = 'Interviewers give one overall mark from 1 to 5. They see these lines as what to look for; HR review scores them.';

/** F5.22: the scoring matrix; each round's scorecard is made from it on the server (JB-14). */
export function MatrixSection({ job, isReadOnly }: SectionProps): ReactNode {
  const section = useMatrixSection(job);
  return (
    <div className={styles.section}>
      <Card title="Scoring matrix" meta="What interviewers score, 1 to 5" action={missingAction(sectionMissing(job, 'matrix'))}>
        {/* ADR 0077: interviewers give one overall mark, so no round reads the matrix any more — only HR review. */}
        <MatrixTable
          rows={section.rows}
          roundCount={0}
          onCommit={section.commit}
          onRowsChange={section.setRows}
          isReadOnly={isReadOnly}
          noRoundsNote={MATRIX_NO_ROUNDS_NOTE}
        />
      </Card>
    </div>
  );
}

/**
 * JB-15 and JB-16 (C4, ADR 0077): how many tests and interviews — set up later, in their own steps —
 * and the pipeline's names.
 */
export function StagesSection(props: Omit<StepProps, 'footer'>): ReactNode {
  // R1 (§L34): time limits belong to SLA timers; off, the fold names only what is in it.
  const isSlaOn = useModule('sla');
  return (
    <div className={styles.section}>
      <StageCounters {...props} footer={reportAs('stages:counts')} />
      <MoreOptions label={isSlaOn ? 'Stage names and time limits' : 'Stage names'}>
        <PipelineStep {...props} footer={reportAs('stages:pipeline')} />
      </MoreOptions>
    </div>
  );
}

/** A step's footer on JOB-13: it reports to the page's sticky footer instead of drawing its own. */
export function reportAs(source: string): (controls: StepControls) => ReactNode {
  return function SaveReport(controls: StepControls): ReactNode {
    return <ReportSave source={source} controls={controls} />;
  };
}

function ReportSave({ source, controls }: { readonly source: string; readonly controls: StepControls }): null {
  useReportSaveState(source, controls.state, controls.flush);
  return null;
}
